import axios, { Axios, InternalAxiosRequestConfig } from "axios"
import { BASE_API_URL } from "./_requests"
import { useRef } from "react"

const AUTH_USER_LOCAL_STORAGE_KEY = 'rc-user-saved-token-v6'
const CURRENT_USER_ACCOUNT_KEY = 'rc-current-user-membership-v6'
const CURRENT_LANGUAGE_KEY = 'rc-current-language-v6'
const CURRENT_ACCOUNT_UNIQUE_IDENTIFIER_KEY = 'rc-current-account-unique-identifier-v6'

export interface AuthModel {
    api_token: string
    refreshToken?: string
}

export class APIError {
    message: string
    response: any

    constructor(message: string, response: any) {
        this.message = message
        this.response = response
    }
}


export const getUserAuth = (): AuthModel | undefined => {
    if (!localStorage) {
        return
    }

    const lsValue: string | null = localStorage.getItem(AUTH_USER_LOCAL_STORAGE_KEY)
    if (!lsValue) {
        return
    }

    try {
        const auth: AuthModel = JSON.parse(lsValue) as AuthModel
        if (auth) {
            // You can easily check auth_token expiration also
            return auth
        }
    } catch (error) {
        console.error('AUTH LOCAL STORAGE PARSE ERROR', error)
    }
}

export const setUserAuth = (auth: AuthModel) => {
    if (!localStorage) {
        return
    }

    try {
        const lsValue = JSON.stringify(auth)
        localStorage.setItem(AUTH_USER_LOCAL_STORAGE_KEY, lsValue)
    } catch (error) {
        console.error('AUTH LOCAL STORAGE SAVE ERROR', error)
    }
}

export const removeAuthCompletely = () => {
    if (!localStorage) {
        return
    }

    // Just clear local storage entirely
    try {
        localStorage.clear()
    } catch (error) {
        console.error('AUTH LOCAL STORAGE CLEAR ERROR', error)
    }
}

export const getCurrentAccountUniqueIdentifierInLocalStorage = (): string | undefined => {
    if (!localStorage) {
        return
    }

    const lsValue: string | null = localStorage.getItem(CURRENT_USER_ACCOUNT_KEY)
    if (!lsValue) {
        return
    }
    return lsValue.toString()
}

export const setCurrentAccountUniqueIdentifierInLocalStorage = (accountUniqueId: string) => {
    if (!localStorage) {
        return
    }
    try {
        localStorage.setItem(CURRENT_USER_ACCOUNT_KEY, accountUniqueId)
    } catch (error) {
        console.error('AUTH LOCAL STORAGE SAVE ERROR', error)
    }
}

export const getCurrentLanguage = (): string => {
    if (!localStorage) {
        return 'en-US'
    }

    const lsValue: string | null = localStorage.getItem(CURRENT_LANGUAGE_KEY)
    if (!lsValue) {
        return 'en-US'
    }
    return lsValue.toString()
}

export const setCurrentLanguage = (language: string) => {
    if (!localStorage) {
        return
    }
    try {
        localStorage.setItem(CURRENT_LANGUAGE_KEY, language)
    } catch (error) {
        console.error('CURRENT LANGUAGE LOCAL STORAGE SAVE ERROR', error)
    }
}

export const getCurrentlySelectedAccountUniqueIdentifierFromLocalStorage = (): string | undefined => {
    if (!localStorage) {
        return
    }
    const lsValue: string | null = localStorage.getItem(CURRENT_ACCOUNT_UNIQUE_IDENTIFIER_KEY)
    if (!lsValue) {
        return
    }
    return lsValue.toString()
}

export const setCurrentlySelectedAccountUniqueIdentifierInLocalStorage = (accountUniqueId: string) => {
    if (!localStorage) {
        return
    }

    try {
        localStorage.setItem(CURRENT_ACCOUNT_UNIQUE_IDENTIFIER_KEY, accountUniqueId)
    } catch (error) {
        console.error('CURRENT ACCOUNT UNIQUE IDENTIFIER LOCAL STORAGE SAVE ERROR', error)
    }
}

function createAxiosInstance(): Axios {
    const axiosInstance = axios.create()
    axiosInstance.defaults.baseURL = BASE_API_URL
    axiosInstance.defaults.headers.Accept = 'application/json'
    axiosInstance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
        const auth = getUserAuth()
        if (auth && auth.api_token) {
            config.headers.Authorization = `Bearer ${auth.api_token}`
        }
        // This no longer happens here. Instead, it happens with every request.
        // We are moving toward explicitness in the request.
        const currentAccountUniqueId = getCurrentAccountUniqueIdentifierInLocalStorage()
        let requestingPath = config.url?.replace(config.baseURL, '')
        // if requesting path contains /V2_API/, replace /V2_API/ with /api/v2/app
        // If path already starts with /api/v1 or /api/v2, don't prefix
        // otherwise, prefix with /api/v1/app
        if (requestingPath?.startsWith('/V2_API/')) {
            requestingPath = requestingPath.replace('/V2_API/', '/api/v2/app/')
        } else if (!requestingPath?.startsWith('/api/')) {
            requestingPath = `/api/v1/app${requestingPath}`
        }
        config.url = requestingPath
        // The only authenticated route that doesn't require a membership is the one to get the user
        // initially, which contains information on memberships.
        if (requestingPath !== '/api/v2/app/auth/me' && currentAccountUniqueId) {
            config.headers['X-Account-Unique-Id'] = currentAccountUniqueId
        } else if (requestingPath !== '/api/v2/app/auth/me' && !currentAccountUniqueId) {
            // When viewing all accounts, we still need to send the header with "all-accounts"
            // for certain endpoints that support it
            const allAccountsEndpoints = [
                '/api/v1/app/authenticated/current-account/received-emails',
                '/api/v1/app/authenticated/current-account/email-campaigns'
            ];
            if (allAccountsEndpoints.some(endpoint => requestingPath?.startsWith(endpoint))) {
                config.headers['X-Account-Unique-Id'] = 'all-accounts'
            }
        }
        const currentLanguage = getCurrentLanguage()
        if (currentLanguage && currentLanguage !== 'en-US') {
            config.headers['X-Current-Language'] = currentLanguage
        }
        return config
    }, (err: any) => Promise.reject(err))
    axiosInstance.interceptors.response.use(
        function (response: any) {
            // Any status code that lie within the range of 2xx cause this function to trigger
            // Do something with response data
            return response
        },
        function (error: any) {
            console.warn('axios error', error)
            // If 404, setup custom error message
            if (error.response && error.response.status === 404) {
                const route = error.config.url.replace(error.config.baseURL, '')
                return Promise.reject(new APIError(`Not found: ${route}`, error.response))
            } else if (error?.response?.data?.error) {
                if (typeof error?.response?.data?.error === 'string') {
                    return Promise.reject(new APIError(error.response.data.error, error.response))
                } else if (
                    error?.response?.data?.error.message &&
                    typeof error?.response?.data?.error.message === 'string'
                ) {
                    return Promise.reject(new APIError(error?.response?.data?.error.message, error.response))
                } else {
                    console.warn('Unexpected API error format')
                    return Promise.reject(new APIError('Unknown API error', error.response))
                }
            }
            // If Network Error, setup custom error message
            if (error.message === 'Network Error') {
                return Promise.reject(new APIError('Network Error', error.response))
            }
            console.warn('axios error', error)
            return Promise.reject(error)
        }
    )
    return axiosInstance
}

export function useAxios() {
    const axios = useRef(createAxiosInstance()).current

    return axios
}